import { getCurrentLocaleCode } from "@mongez/localization";
import { normalizeLatinDigits } from "./locale-formatters";
import { getZonedDateParts } from "./platform-time-zone";

const monthNamesInArabicMap = {
  September: "سبتمبر",
  October: "اكتوبر",
  November: "نوفمبر",
  December: "ديسمبر",
  January: "يناير",
  February: "فبراير",
  March: "مارس",
  April: "أبريل",
  May: "مايو",
  June: "يونيو",
  July: "يوليو",
  August: "أغسطس",
};

// All helpers below read the calendar day in the platform timezone (see
// platform-time-zone.ts), never the process' local zone, so SSR and the
// browser render identical text.

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * Format date for display in a user-friendly way
 * Handles both English and Arabic locales properly
 */
export function formatDateForDisplay(timestamp: number): string {
  const locale = getCurrentLocaleCode();
  const { day, year, monthName, shortMonthName } =
    getZonedDateParts(timestamp);

  if (locale === "ar") {
    return normalizeLatinDigits(
      `${pad2(day)} ${monthNamesInArabicMap[monthName as keyof typeof monthNamesInArabicMap]} ${year}`,
    );
  }

  return normalizeLatinDigits(`${pad2(day)} ${shortMonthName} ${year}`);
}

/**
 * Format date with full month name for better readability
 */
export function formatDateWithFullMonth(timestamp: number): string {
  const { day, year, monthName } = getZonedDateParts(timestamp);

  return normalizeLatinDigits(`${pad2(day)} ${monthName} ${year}`);
}

/**
 * Format date in a compact way (e.g., "15 Jan")
 */
export function formatDateCompact(timestamp: number): string {
  const { day, shortMonthName } = getZonedDateParts(timestamp);

  return normalizeLatinDigits(`${pad2(day)} ${shortMonthName}`);
}
